import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import html2pdf from "html2pdf.js";
import Logo from "../assest/oakyalogo.png";
import displayINRCurrency from "../helpers/displayCurrency";
import SummaryApi from "../common";

const AllInvoice = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [student, setStudent] = useState(location.state?.student || null);
  const [loading, setLoading] = useState(false);

  const urlStudentId = searchParams.get("student_id");
  const urlPaymentId = searchParams.get("payment_id");

  useEffect(() => {
    // Always try to fetch fresh/complete data using student_id (+ payment_id if present)
    // This covers: page refresh, direct URL, and cases where only partial data was passed via state.
    const stateStudentId = location.state?.student?.student_id;
    const stateStudent = location.state?.student;
    const statePayment = location.state?.payment;

    const studentIdToUse = urlStudentId || stateStudentId;
    const paymentIdToUse = urlPaymentId || statePayment?.payment_id;

    if (studentIdToUse) {
      fetchInvoiceData(studentIdToUse, paymentIdToUse, stateStudent, statePayment);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchInvoiceData = async (studentId, paymentId, fallbackStudent, fallbackPayment) => {
    setLoading(true);
    try {
      // 1. Fetch full student details
      const studentPromise = fetch(SummaryApi.getstudentbyId.url, {
        method: SummaryApi.getstudentbyId.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_id: studentId }),
      })
        .then((res) => res.json())
        .catch((err) => {
          console.error("Error fetching student info:", err);
          return null;
        });

      // 2. Fetch payment details (only if we have a payment id)
      const paymentPromise = paymentId
        ? fetch(SummaryApi.getpaymentbyid.url, {
            method: SummaryApi.getpaymentbyid.method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ payment_id: paymentId, student_id: studentId }),
          })
            .then((res) => res.json())
            .catch((err) => {
              console.error("Error fetching payment info:", err);
              return null;
            })
        : Promise.resolve(null);

      const [studentResult, paymentResult] = await Promise.all([
        studentPromise,
        paymentPromise,
      ]);

      // studentResult.data may come back as an object OR an array depending on API shape
      let studentData = null;
      if (studentResult?.status && studentResult?.data) {
        studentData = Array.isArray(studentResult.data)
          ? studentResult.data.find((s) => String(s.student_id) === String(studentId)) ||
            studentResult.data[0]
          : studentResult.data;
      }

      const paymentData =
        paymentResult?.status && paymentResult?.data ? paymentResult.data : null;

      // Merge priority: fresh student data > fresh payment data > fallback (state) data
      // This guarantees every dynamic field on the invoice is populated from the best available source.
      const merged = {
        ...fallbackStudent,
        ...fallbackPayment,
        ...studentData,
        ...paymentData,
        student_id: studentId,
      };

      if (!studentData && !paymentData && !fallbackStudent && !fallbackPayment) {
        console.error("No student or payment data could be resolved for invoice.");
      }

      setStudent(merged);
    } catch (error) {
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <p>Loading invoice...</p>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 font-semibold">No student data found.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 text-blue-600 underline"
        >
          Go back
        </button>
      </div>
    );
  }

  const handleDownloadPDF = () => {
    const element = document.getElementById("invoice-content");
    const originalStyle = element.getAttribute("style") || "";

    element.style.transform = "scale(0.8)";
    element.style.transformOrigin = "top left";
    element.style.width = "1200px";
    element.style.height = "auto";

    const options = {
      margin: 0.5,
      filename: `Invoice_${(student.full_name || "Invoice").replace(/\s/g, "_")}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "landscape" },
    };

    html2pdf()
      .set(options)
      .from(element)
      .save()
      .then(() => {
        element.setAttribute("style", originalStyle);
      })
      .catch((err) => {
        console.error("PDF generation failed:", err);
        element.setAttribute("style", originalStyle);
      });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handlePrint = () => {
    const printStyle = `
    @media print {
      @page {
        margin: 8mm 5mm 8mm 5mm;
        size: auto;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
      }
      body * {
        visibility: hidden;
      }
      #invoice-content, #invoice-content * {
        visibility: visible;
      }
      #invoice-content {
        position: absolute;
        left: 0;
        top: 0;
        font-size: 14px !important;
        width: 100% !important;
        padding: 0 3mm;
      }
      #invoice-content .flex.justify-between {
        gap: 2rem !important;
        margin-bottom: 12px !important;
      }
      #invoice-content .flex.justify-between > div {
        width: 45% !important;
      }
      #invoice-content .text-xs {
        font-size: 12px !important;
      }
      #invoice-content .text-[10px] {
        font-size: 11px !important;
      }
      #invoice-content .text-sm {
        font-size: 13px !important;
      }
      #invoice-content .text-lg {
        font-size: 16px !important;
      }
      #invoice-content h1 {
        font-size: 18px !important;
      }
      #invoice-content h2 {
        font-size: 15px !important;
      }
      #invoice-content p {
        font-size: 13px !important;
        line-height: 1.5 !important;
      }
      #invoice-content strong {
        font-size: 13px !important;
      }
      #invoice-content table {
        font-size: 12px !important;
      }
      #invoice-content table th,
      #invoice-content table td {
        font-size: 12px !important;
        padding: 6px 8px !important;
      }
      #invoice-content .mb-4 {
        margin-bottom: 16px !important;
      }
      #invoice-content .mb-10 {
        margin-bottom: 20px !important;
      }
      #invoice-content .mt-4 {
        margin-top: 16px !important;
      }
      #invoice-content .gap-2 {
        gap: 10px !important;
      }
      #invoice-content .p-6 {
        padding: 18px !important;
      }
    }
  `;

    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = printStyle;
    document.head.appendChild(styleSheet);

    window.print();

    setTimeout(() => {
      document.head.removeChild(styleSheet);
    }, 1000);
  };

  const totalFee = Number(student.total_fee) || 0;
  const paidFee = Number(student.paid_fee) || 0;
  const balanceFee =
    student.balance_fee !== undefined && student.balance_fee !== null
      ? Number(student.balance_fee)
      : totalFee - paidFee;

  const programLabel = [
    "CSE",
    "AI & ML",
    "Aeronautical Engineering",
    "ECE",
    "CSE(AI & ML)",
  ].includes(student.course)
    ? "B Tech"
    : "Degree";

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="print:hidden flex justify-center gap-4 mb-6">
        <button
          onClick={handlePrint}
          className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700"
        >
          Print
        </button>
        <button
          onClick={handleDownloadPDF}
          className="bg-green-600 text-white py-2 px-4 rounded hover:bg-green-700"
        >
          Download PDF
        </button>
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-600 text-white py-2 px-4 rounded hover:bg-gray-700"
        >
          Back
        </button>
      </div>

      <div
        id="invoice-content"
        className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3"
      >
        {["Admin Copy", "Accountant Copy", "Student Copy"].map((copyType) => (
          <div
            key={copyType}
   className="bg-white border border-black rounded-md shadow p-6 text-base"
          >
            <div className="text-center mb-4">
              <div className="flex items-center justify-center gap-2 mb-2">
                <img src={Logo} alt="Logo" className="h-8" />
                <h1 className="text-lg font-bold">OAKYA Hospitality</h1>
              </div>
              <p className="text-black font-semibold mt-[-3%]">{copyType}</p>
              <h1 className="text-lg font-bold print:text-sm">
                [AY-2026-2027]
              </h1>
            </div>

            <div className="flex justify-between">
              <div className="mb-4">
                <h2 className="font-semibold mb-2">
                  Date : {formatDate(student.created_date || student.payment_date)}
                </h2>
                <p>
                  <strong>PRN No:</strong> {student.student_admission_number || "N/A"}
                </p>
                <p>
                  <strong>Stream:</strong> {student.course || "N/A"}
                </p>
                <p>
                  <strong>Name:</strong> {student.full_name || "N/A"}
                </p>
              </div>
              <div className="mb-4" id="">
                <p>
                  <strong>Invoice Number:</strong> {student.invoice_number || "N/A"}
                </p>
                <p>
                  <strong>Program:</strong> {programLabel}
                </p>
                <p>
                  <strong>Year:</strong> {student.year || "N/A"} - {student.semester || "N/A"}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <table className="w-full border border-gray-300 text-xs">
                {/* <thead>
                  <tr>
                    <th rowSpan={2} className="border px-2 py-1 align-middle">
                      1st Semester UAA Details
                    </th>
                    <th colSpan={2} className="border px-2 py-1 text-center">
                      Paid Amount INR
                    </th>
                  </tr>
                  <tr>
                    <th className="border px-2 py-1">Cash</th>
                    <th className="border px-2 py-1">Online</th>
                  </tr>
                </thead> */}
                <tbody>
                  {[
                    // {
                    //   label: "UAA (university admission assistance) Total Fee",
                    //   value: displayINRCurrency(totalFee),
                    // },
                    {
                      label: "Paid Fee",
                      value: displayINRCurrency(paidFee),
                    },
                    // {
                    //   label: "Balance Fee",
                    //   value: displayINRCurrency(balanceFee),
                    // },
                    // {
                    //   label: "Due Date",
                    //   value: formatDate(student.due_date),
                    //   isTotal: true,
                    // },
                  ].map((item, index) => (
                    <tr
                      key={index}
                      className={
                        item.isTotal ? "bg-gray-100 font-semibold" : ""
                      }
                    >
                      <td className="border px-2 py-1" style={{ verticalAlign: "middle" }}>{item.label}</td>

                      {/* {student.payment_method === "Cash" ? (
                        <>
                          <td className="border px-2 py-1 text-right">
                            {item.value}
                          </td>
                          <td className="border px-2 py-1"></td>
                        </>
                      ) : (
                        <>
                          <td className="border px-2 py-1"></td>
                          <td className="border px-2 py-1 text-right">
                            {item.value}
                          </td>
                        </>
                      )} */}
    <td className="border px-2 py-1 text-right align-middle" style={{ verticalAlign: "middle" }}>
                            {item.value}
                          </td>
                          {/* <td className="border px-2 py-1"></td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-10">
              <p>
                <strong>Payment Terms:</strong> {student.payment_method || "N/A"}
              </p>
              <p>
                <strong>Transaction Id:</strong>{" "}
                {student.transaction_id || "N/A"}
              </p>
              {student.remarks && (
                <p>
                  <strong>Remarks:</strong> {student.remarks}
                </p>
              )}
            </div>

            {/* <div className="text-right text-xs text-gray-600 ml-auto w-fit">
              <p>Authorized Signature</p>
              <p className="font-bold">OAKYA</p>
            </div> */}
<div>
<p className="text-right text-xs text-gray-600">
  This is an electronically generated receipt.
</p>
  </div>
            <div className="text-gray-600 mt-4 text-center">
              <p className="text-[10px] text-center">
                <strong>Note:</strong> I confirm that I have been
                admitted/enrolled through <strong>OAKYA</strong> the <strong>Sanjivani</strong> Authorised Official
                Channel Partner for AP & Telangana for the academic years
                2026–2030.
              </p>
              {/* <p></p> */}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllInvoice;